import React from 'react';
import { AlertTriangle, Info, Trash2, X } from 'lucide-react';

/**
 * Dialog di conferma nel design system dell'area associativa (.assoc-app).
 * Stesse props di ConfirmDialog: sostituzione drop-in per le pagine
 * renderizzate dentro AssociativeAppShell.
 */
interface AssocConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  icon?: React.ReactNode;
}

const TYPE_META = {
  danger: { color: 'var(--un-danger)', bg: 'rgba(217,75,59,.12)', btn: 'danger', icon: <Trash2 size={18} /> },
  warning: { color: 'var(--un-warning)', bg: 'rgba(232,165,42,.14)', btn: 'warn', icon: <AlertTriangle size={18} /> },
  info: { color: 'var(--un-green-600)', bg: 'var(--un-green-50)', btn: 'primary', icon: <Info size={18} /> },
} as const;

const AssocConfirmDialog: React.FC<AssocConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Conferma',
  cancelText = 'Annulla',
  type = 'danger',
  icon,
}) => {
  if (!isOpen) return null;

  const meta = TYPE_META[type];

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal" role="alertdialog" aria-modal="true" aria-label={title} style={{ maxWidth: '440px' }}>
        <div className="modal-head">
          <span
            style={{
              width: '34px', height: '34px', borderRadius: '50%', flex: '0 0 auto',
              display: 'grid', placeItems: 'center',
              background: meta.bg, color: meta.color,
            }}
          >
            {icon || meta.icon}
          </span>
          <h3>{title}</h3>
          <button className="modal-x" type="button" onClick={onClose} aria-label="Chiudi">
            <X size={16} />
          </button>
        </div>
        <div className="modal-body">
          <p style={{ margin: 0, font: '400 13.5px/1.6 var(--font-body)', color: 'var(--text-body)' }}>
            {message}
          </p>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn ghost sm" onClick={onClose}>
            {cancelText}
          </button>
          <button type="button" className={`btn ${meta.btn} sm`} onClick={handleConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssocConfirmDialog;
